import { styled } from "@mui/system";
import { ObjectId } from "bson";
import React from "react";

/* Redux Imports */

import StateInterface from "../../../../../redux-magic/state-interface";
import { DbUserPage } from "../../../../../redux-magic/sub-interfaces/user-interface";
import {
    setCreateNewPropertyUploadImagesThunk,
    setPropertyImagesUploadCompletedThunk,
    setPropertyIsImagesUploadingThunk,
} from "../../../../../redux-magic/thunks";

/* Component Imports */

import {
    Alert,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    RadioGroup,
    Snackbar,
    SvgIcon,
    Typography,
    useTheme,
} from "@mui/material";
import { useRouter } from "next/router";
import uploadFiles from "../../../../../lib/uploadFiles";
import validateFiles from "../../../../../lib/validateFiles";
/* Icon Imports */

import { ChevronLeft, ChevronRight, DeleteOutline, Refresh } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RequestImagesIcon from "../../../../../public/images/icons/singleproperty/request_image.svg";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";
const displayImage: string = "/images/uploads/displayImage.jpg";

/* Interfaces */

interface FileAndMore {
	file: File;
	mime_type: string;
}

interface PhotoItem {
	key: string;
	/* What the tile shows: a local preview, or the saved photo's link when resumed. */
	preview: string;
	/* The picked file, kept for a retry; null for a photo that was already uploaded. */
	file: FileAndMore | null;
	file_id: string | null;
	mime_type: string;
	category: string;
	status: "uploading" | "done" | "failed";
}

let photoKeySeed: number = 0;
const newPhotoKey = (): string => "photo-" + Date.now() + "-" + photoKeySeed++;

/* Styled Components */

const CustomDialog = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
}));

const CustomDialogContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0rem",
	padding: "1rem 0rem 0rem 0rem",
	background: theme.palette.background.paper,
}));

const InputContent = styled("input")(({ theme }) => ({
	display: "none",
}));

const UploadContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	border: "1px dashed var(--Other-New-Outline-Border, rgba(0, 0, 0, 0.23))",
	borderRadius: "1rem",
	cursor: "pointer",
	borderColor: "#0000003B",
	// height: "10rem",
	padding: "2rem",
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "47%",
	height: "10.5rem",
	borderRadius: "16px",
	borderColor: theme.palette.primary.main,
}));

const SecondaryUploadContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "15rem",
	height: "5.238rem",
	border: "2px dashed #EE4B2B",
	borderRadius: "16px",
	cursor: "pointer",
	borderColor: theme.palette.primary.main,
}));

const ImageContainer = styled("div")(({ theme }) => ({
	position: "relative",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
}));

const PreviewImage = styled("img")(({ theme }) => ({
	width: "100%",
	height: "10.238rem",
	borderRadius: "16px ",
	// border: "1px dashed rgba(0, 0, 0, 0.40)",
	// borderColor: theme.palette.primary.main,
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	[theme.breakpoints.down("md")]: {
		bottom: "5rem",
		borderRadius: "0.5rem",
	},
}));

const FormControlContainer = styled(FormControl)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	padding: "var(--Spacing-spacing-sm, 0.5rem)",
	gap: "var(--Spacing-spacing-sm, 0.5rem)",
	alignSelf: "stretch",
	width: "100%",
	height: "3rem",
	position: "absolute",
	bottom: "0.3rem",
}));

const ProgressContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	position: "absolute",
	width: "100%",
	height: "100%",
	zIndex: 0,
}));

const ImagePreviewContainer = styled(RadioGroup)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	maxHeight: "30rem",
	gap: "1rem",
	flexWrap: "wrap",
}));

const DividerContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "center",
	gap: "8px",
	width: "100%",
	margin: "0rem",
}));

const UploadImages = ({
	newPropertyData,
	session,
	dispatch,
	businessProfiles,
	isBusinessProfile,
	isNewBusinessProfile,
}: {
	newPropertyData: StateInterface["new_property_data"];
	session: StateInterface["redux_session"];
	dispatch: Function;
	businessProfiles: DbUserPage;
	isBusinessProfile: boolean;
	isNewBusinessProfile: boolean;
}) => {
	const theme = useTheme();

	const router = React.useRef(useRouter()).current;

	const localLang = {
		sizeOrTypeNotSupported:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಫೈಲ್ ಗಾತ್ರ ಅಥವಾ ಪ್ರಕಾರ ಬೆಂಬಲಿಸಲ್ಪಟ್ಟಿಲ್ಲ "
					: "File size or type not supported",
		isAn: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಒಂದು" : " is an",
		are: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಇವು" : " are",
		unsupported: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಅಸಮರ್ಥವಾಗಿದೆ" : " unsupported",
		file: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಫೈಲ್" : " file",
		files: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಫೈಲ್ಗಳು" : " files",
		and: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಮತ್ತು" : " and",
		has: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಹೊಂದಿದೆ" : " has",
		have: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಹೊಂದಿದುವು" : " have",
		beenDeselected:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? " ಸ್ವಯಂಚಾಲಿತವಾಗಿ ರದ್ದುಗೊಂಡಿದೆ."
					: " been deselected automatically.",
		jpegOrPng: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "JPEG ಅಥವಾ PNG" : "JPEG or PNG",
		imgMaxSize:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಚಿತ್ರ ಗಾತ್ರ ಗರಿಷ್ಠ 10MB" : "Max upload 10MB",
		category: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ವರ್ಗ" : "Category",
		exterior: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಬಾಹ್ಯ" : "Exterior",
		interior: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಆಂತರಿಕ" : "Interior",
		masterPlan: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಮಾಸ್ಟರ್ ಪ್ಲಾನ್" : "Master plan",
		amenities: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಸೌಲಭ್ಯಗಳು" : "Amenities",
		routMap: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ರೂಟ್ ಮ್ಯಾಪ್" : "Route map",
		addMore: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಹೆಚ್ಚು ಸೇರಿಸಿ" : "Add More",
		addPhotosFromYourGallary:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಗ್ಯಾಲರಿ ನಿಂದ ಫೋಟೋಗಳನ್ನು ಸೇರಿಸಿ"
					: "Add photos from your gallery",
		maxLimit:
			router.locale === "ar-AE"
				? "ملف JPG أو PNG، بحجم لا يزيد عن 10 ميجابايت."
				: router.locale === "kn-IN"
					? "JPG ಅಥವಾ PNG ಫೈಲ್, ಗಾತ್ರವು 10MB ಗಿಂತ ಕಡಿಮೆ ಇರಬೇಕು."
					: "PG or PNG, file size no more than 10MB",
		moreThePixelMoreTheReach:
			router.locale === "ar-AE"
				? "نصيحة احترافية: قم بتحميل صور عالية الجودة للحصول على المزيد من العملاء المحتملين وبيع أسرع."
				: router.locale === "kn-IN"
					? "ಪ್ರೊ ಟಿಪ್: ಹೆಚ್ಚಿನ ಲೀಡ್‌ಗಳನ್ನು ಪಡೆಯಲು ಗುಣಮಟ್ಟದ ಚಿತ್ರಗಳನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ ಮತ್ತು ವೇಗವಾಗಿ ಮಾರಾಟ ಮಾಡಿ."
					: "Pro Tip: Upload quality images to get more leads and sell faster",
		startUploading:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಅಪ್ಲೋಡ್ ಪ್ರಾರಂಭಿಸಿ" : "Start Uploading",
		coverImageText:
			router.locale === "ar-AE"
				? "اضغط على صورة لجعلها الغلاف، أو اسحبها أو استخدم الأسهم لتغيير الترتيب"
				: router.locale === "kn-IN"
					? "ಕವರ್ ಮಾಡಲು ಫೋಟೋವನ್ನು ಟ್ಯಾಪ್ ಮಾಡಿ, ಅಥವಾ ಕ್ರಮ ಬದಲಿಸಲು ಎಳೆಯಿರಿ ಅಥವಾ ಬಾಣಗಳನ್ನು ಬಳಸಿ"
					: "Tap a photo to make it the cover, or drag it or use the arrows to change the order",
		uploaded: router.locale === "ar-AE" ? "تم التحميل" : router.locale === "kn-IN" ? "ಅಪ್ಲೋಡ್ ಮಾಡಲಾಗಿದೆ" : "Uploaded",
		moveEarlier: router.locale === "ar-AE" ? "نقل للأمام" : router.locale === "kn-IN" ? "ಮುಂದಕ್ಕೆ ಸರಿಸಿ" : "Move earlier",
		moveLater: router.locale === "ar-AE" ? "نقل للخلف" : router.locale === "kn-IN" ? "ಹಿಂದಕ್ಕೆ ಸರಿಸಿ" : "Move later",
		tapToRetry:
			router.locale === "ar-AE" ? "اضغط لإعادة المحاولة" : router.locale === "kn-IN" ? "ಮರುಪ್ರಯತ್ನಿಸಲು ಟ್ಯಾಪ್ ಮಾಡಿ" : "Tap to retry",
		someFailed:
			router.locale === "ar-AE"
				? "لم يتم تحميل بعض الصور. أعد المحاولة أو احذفها للمتابعة."
				: router.locale === "kn-IN"
					? "ಕೆಲವು ಫೋಟೋಗಳು ಅಪ್‌ಲೋಡ್ ಆಗಲಿಲ್ಲ. ಮುಂದುವರಿಯಲು ಮರುಪ್ರಯತ್ನಿಸಿ ಅಥವಾ ಅವುಗಳನ್ನು ತೆಗೆದುಹಾಕಿ."
					: "Some photos didn't upload. Retry or remove them to continue.",
	};

	const input = React.useRef<HTMLInputElement>(null);

	/*

	& One entry per photo, in the listing's order: the first is the cover (priority 0), as in the app. Each photo
	& uploads on its own, so one failure no longer hangs the step: it shows "Tap to retry" and can be removed
	& (the app's PropertyPhotosStep). Removing a photo still uploading drops it; its upload's answer is ignored.

	*/

	const [photos, setPhotos] = React.useState<Array<PhotoItem>>(() =>
		[...(newPropertyData?.images || [])]
			.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
			.map((image) => ({
				key: newPhotoKey(),
				preview: image.caption,
				file: null,
				file_id: image.file_id ? String(image.file_id) : null,
				mime_type: image.mime_type,
				category: image.category || "exterior",
				status: "done" as const,
			})),
	);

	const [openSnackbar, setOpenSnackbar] = React.useState(false);

	const [fileErrorSnackbarText, setFileErrorSnackbarText] = React.useState<string>(localLang.sizeOrTypeNotSupported);

	const [isFileDialogOpen, setFileDialogOpen] = React.useState(false);

	const [step, setStep] = React.useState<number>(0);

	const donePhotos = photos.filter((photo) => photo.status === "done");
	const failedCount = photos.filter((photo) => photo.status === "failed").length;

	/* The form keeps only the uploaded photos, cover first; Next waits while any photo is uploading or failed. */

	React.useEffect(() => {
		dispatch(
			setCreateNewPropertyUploadImagesThunk(
				donePhotos.map((photo, index) => ({
					file_id: photo.file_id || "",
					mime_type: photo.mime_type,
					category: photo.category,
					priority: index,
					caption: photo.preview,
					stage: "FETCHED_FILE_INFORMATION",
				})),
			),
		);
		dispatch(setPropertyIsImagesUploadingThunk(photos.length !== donePhotos.length));
		dispatch(setPropertyImagesUploadCompletedThunk(donePhotos.length > 0 && photos.length === donePhotos.length));
		// dispatch is stable; the photos list is the only input.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [photos]);

	const updatePhoto = (key: string, change: Partial<PhotoItem>) =>
		setPhotos((prev) => prev.map((photo) => (photo.key === key ? { ...photo, ...change } : photo)));

	const uploadPhoto = async (key: string, file: FileAndMore) => {
		let uploaded: any = null;
		let expired: boolean = false;
		try {
			const result = await uploadFiles(
				session ? (session.authorized ? (session.session_id ? session.session_id : "") : "") : "",
				[
					{
						file: file.file,
						mime_type: file.mime_type,
						original_file_id: new ObjectId(),
						original_file_server_path: "",
						original: true,
						is_business_profile: isBusinessProfile || isNewBusinessProfile ? true : false,
						business_profile_id: businessProfiles ? new ObjectId(businessProfiles.page_id) : new ObjectId(),
						entity: "property_regular",
						entity_id: new ObjectId(),
						use_case: "property_image",
						uiPath: "/",
						isPublic: false,
						text: businessProfiles ? businessProfiles.page_title : "",
					},
				],
				(progress: Array<{ stage: string }>) => {
					if (progress[0]?.stage === "PRESIGNED_URL_EXPIRED") expired = true;
				},
			);
			uploaded = result?.uploadedFiles?.[0] || null;
		} catch {
			uploaded = null;
		}
		/* Done only once the server says the file arrived: an expired link or a refused request leaves it "prepared". */
		const arrived: boolean =
			!expired && Boolean(uploaded?._id) && !["prepared", "uploading", "failed"].includes(String(uploaded?.stage || ""));
		updatePhoto(key, arrived ? { status: "done", file_id: String(uploaded._id) } : { status: "failed" });
	};

	const handleUpload = (validFiles: Array<FileAndMore>, previews: Array<string>) => {
		const added: Array<PhotoItem> = validFiles.map((file, index) => ({
			key: newPhotoKey(),
			preview: previews[index],
			file: file,
			file_id: null,
			mime_type: file.mime_type,
			category: "exterior",
			status: "uploading",
		}));
		setPhotos((prev) => [...prev, ...added]);
		added.forEach((photo) => photo.file && uploadPhoto(photo.key, photo.file));
	};

	const handleRetry = (photo: PhotoItem) => {
		if (!photo.file) return;
		updatePhoto(photo.key, { status: "uploading" });
		uploadPhoto(photo.key, photo.file);
	};

	/* Tapping an uploaded photo makes it the cover: it moves to the front. */

	const handleMakeCover = (key: string) =>
		setPhotos((prev) => {
			const index = prev.findIndex((photo) => photo.key === key);
			if (index <= 0) return prev;
			return [prev[index], ...prev.slice(0, index), ...prev.slice(index + 1)];
		});

	const handleRemoveImage = (key: string) => setPhotos((prev) => prev.filter((photo) => photo.key !== key));

	/* Reorder (the app's PhotoReorder): drag a photo onto another on a desktop, or the arrows on each photo (touch
	   has no drag-and-drop in the browser). The first photo is the cover. */

	const [draggingKey, setDraggingKey] = React.useState<string>("");

	const movePhoto = (key: string, toIndex: number) =>
		setPhotos((prev) => {
			const from = prev.findIndex((photo) => photo.key === key);
			if (from < 0 || toIndex < 0 || toIndex >= prev.length || from === toIndex) return prev;
			const next = [...prev];
			const [moved] = next.splice(from, 1);
			next.splice(toIndex, 0, moved);
			return next;
		});

	const formatCategory = (category: string) => {
		return category
			.split("_")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ");
	};

	React.useEffect(() => {
		if (isFileDialogOpen && input.current) {
			input.current.value = "";
			input.current.click();
			// Set isFileDialogOpen to false after opening the dialog
			setFileDialogOpen(false);
		}
	}, [isFileDialogOpen]);

	return (
		<React.Fragment>
			<Snack
				open={openSnackbar}
				autoHideDuration={3000}
				onClose={() => setOpenSnackbar(false)}
			>
				<Alert
					onClose={() => setOpenSnackbar(false)}
					severity="error"
					sx={{ width: "100%" }}
				>
					{fileErrorSnackbarText}
				</Alert>
			</Snack>

			<CustomDialog>
				<CustomDialogContent>
					<InputContent
						ref={input}
						type="file"
						accept="image/jpeg, image/png"
						multiple
						onChange={async (event) => {
							const files = event.target.files;
							const allowedTypes = ["image/jpeg", "image/png"];
							const allowedExtensions = ["jpg", "jpeg", "png"];
							const uploadType = "property_or_services";
							if (!files?.length) return setOpenSnackbar(true);

							const { validFiles, invalidFiles } = await validateFiles(
								Array.from(files),
								allowedTypes,
								allowedExtensions,
								uploadType,
							);

							if (validFiles.length) {
								const previews = validFiles.map((f) => URL.createObjectURL(f.file));
								handleUpload(validFiles, previews);
								setStep(0);
							}

							if (invalidFiles.length) {
								const names = invalidFiles.map((f) => f.name).join(", ");
								const display = names.length > 15 ? names.slice(0, 15) + "..." : names;
								setFileErrorSnackbarText(
									`${display} ${invalidFiles.length === 1 ? localLang.isAn : localLang.are}` +
										`${localLang.unsupported}${invalidFiles.length === 1 ? localLang.file : localLang.files}` +
										`${localLang.and}${invalidFiles.length === 1 ? localLang.has : localLang.have}${localLang.beenDeselected}`,
								);
								setOpenSnackbar(true);
							}
						}}
					/>

					{photos.length === 0 && step === 0 && (
						<div style={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
							<UploadContent onClick={() => input.current?.click()}>
								<SvgIcon
									component={RequestImagesIcon}
									viewBox="0 0 41 33"
									width={24}
									height={24}
									sx={{ width: "2.563rem", height: "1.976rem", fill: "none" }}
								/>

								<Typography sx={{ color: theme.palette.text.primary, fontSize: "0.875rem" }}>
									{localLang.addPhotosFromYourGallary}
								</Typography>
								<Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
									{localLang.maxLimit}
								</Typography>
							</UploadContent>
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									backgroundColor: "#var(--Info-New-States-Outlined-Hover-Background, rgba(33, 150, 243, 0.08))",
									border: "1px solid var(--Info-New-States-Outlined-Resting-Border, rgba(33, 150, 243, 0.50))",
									padding: "1rem",
									borderRadius: "1rem",
									gap: "1rem",
								}}
							>
								<EmojiObjectsOutlinedIcon sx={{ color: "#0B79D0" }} />
								<Typography
									sx={{
										color: theme.palette.text.secondary,
										fontSize: "0.75rem",
										fontWeight: 400,
									}}
								>
									{localLang.moreThePixelMoreTheReach}
								</Typography>
							</div>
						</div>
					)}

					{step === 0 && (
						<ImagePreviewContainer>
							{photos.length > 0 && (
								<div
									style={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "space-between",
										alignItems: "center",
										width: "100%",
									}}
								>
									<Typography
										sx={{
											color: theme.palette.text.primary,
											fontSize: "1rem",
											fontWeight: 700,
										}}
									>
										{localLang.uploaded + " "} {donePhotos.length}{" "}
										{donePhotos.length === 1 ? "image" : "images"}
									</Typography>
									<BeegruButton
										variant="contained"
										sx={{
											color: theme.palette.primary.main,
											fontSize: "0.875rem",
											fontWeight: 500,
											textTransform: "none",
										}}
										onClick={() => {
											input.current?.click();
										}}
									>
										<AddOutlinedIcon /> {localLang.addMore}
									</BeegruButton>
								</div>
							)}
							{failedCount > 0 && (
								<Alert
									severity="error"
									sx={{ width: "100%", borderRadius: "0.75rem" }}
								>
									{localLang.someFailed}
								</Alert>
							)}
							{donePhotos.length > 0 && (
								<div style={{ display: "flex", position: "relative", width: "100%" }}>
									<Chip
										label="Cover photo"
										sx={{
											position: "absolute",
											top: "0.5rem",
											left: "0.5rem",
											backgroundColor: "#FFF",
											color: "#7E7D7D",
											fontSize: "0.75rem",
											fontWeight: 400,
											borderRadius: "0.75rem",
											boxShadow: "0px 4px 18px rgba(0,0,0,0.05)",
											height: "20px",
											zIndex: 1,
										}}
									/>
									<PreviewImage
										src={donePhotos[0].preview}
										alt="Property Images"
										referrerPolicy="no-referrer"
										style={{ width: "100%", height: "10.5rem", borderRadius: "16px" }}
									/>
								</div>
							)}

							{photos.map((photo, photoIndex) => (
								<ContentContainer
									key={photo.key}
									draggable={photo.status === "done"}
									onDragStart={() => setDraggingKey(photo.key)}
									onDragEnd={() => setDraggingKey("")}
									onDragOver={(event) => draggingKey && event.preventDefault()}
									onDrop={(event) => {
										event.preventDefault();
										if (draggingKey) movePhoto(draggingKey, photoIndex);
										setDraggingKey("");
									}}
									sx={{ opacity: draggingKey === photo.key ? 0.4 : 1 }}
								>
									{photo.preview && (
										<ImageContainer
											onClick={() =>
												photo.status === "done"
													? handleMakeCover(photo.key)
													: photo.status === "failed"
														? handleRetry(photo)
														: undefined
											}
											sx={{ cursor: photo.status === "uploading" ? "default" : "pointer" }}
										>
											<PreviewImage
												src={photo.preview}
												alt="Property Images"
												referrerPolicy="no-referrer"
												style={{ opacity: photo.status === "done" ? 1 : 0.6 }}
											/>
											<ProgressContainer
												sx={{
													borderRadius: "16px",
													background: photo.status === "failed" ? "rgba(211, 47, 47, 0.35)" : "transparent",
												}}
											>
												{photo.status === "uploading" ? <CircularProgress sx={{ color: "#FFF" }} /> : null}
												{photo.status === "failed" ? (
													<Chip
														icon={<Refresh sx={{ fontSize: "1rem" }} />}
														label={localLang.tapToRetry}
														size="small"
														sx={{ backgroundColor: "#FFF", color: "#D32F2F", fontWeight: 500 }}
													/>
												) : null}
											</ProgressContainer>
											{/* Remove on every photo: a failed one, or one still uploading (that cancels it). */}
											<IconButton
												size="small"
												onClick={(event) => {
													event.stopPropagation();
													handleRemoveImage(photo.key);
												}}
												sx={{
													position: "absolute",
													top: "0.3rem",
													right: "0.5rem",
													background: "#FFFFFF90",
													padding: "0.5rem",
													height: "2rem",
													width: "2rem",
													zIndex: 1,
												}}
											>
												<DeleteOutline sx={{ color: "#E31B0C", fontSize: "1.25rem" }} />
											</IconButton>
											{photos.length > 1 ? (
												<div
													style={{ position: "absolute", bottom: "0.3rem", left: "0.5rem", display: "flex", gap: "0.25rem", zIndex: 1 }}
												>
													<IconButton
														size="small"
														aria-label={localLang.moveEarlier}
														disabled={photoIndex === 0}
														onClick={(event) => {
															event.stopPropagation();
															movePhoto(photo.key, photoIndex - 1);
														}}
														sx={{ background: "#FFFFFFCC", height: "1.75rem", width: "1.75rem", "&:hover": { background: "#FFFFFF" } }}
													>
														<ChevronLeft sx={{ fontSize: "1.25rem" }} />
													</IconButton>
													<IconButton
														size="small"
														aria-label={localLang.moveLater}
														disabled={photoIndex === photos.length - 1}
														onClick={(event) => {
															event.stopPropagation();
															movePhoto(photo.key, photoIndex + 1);
														}}
														sx={{ background: "#FFFFFFCC", height: "1.75rem", width: "1.75rem", "&:hover": { background: "#FFFFFF" } }}
													>
														<ChevronRight sx={{ fontSize: "1.25rem" }} />
													</IconButton>
												</div>
											) : null}
										</ImageContainer>
									)}
								</ContentContainer>
							))}
						</ImagePreviewContainer>
					)}

					{/* {step === 1 && (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								gap: "1rem",
								width: "100%",

								animation: "fadeIn 0.5s ease-in-out",
							}}
						>
							<img
								src={displayImage}
								alt="Display Image"
								style={{ width: "100%", height: "auto" }}
							/>
							<Typography sx={{ color: theme.palette.text.primary, fontSize: "1.5rem", fontWeight: 700 }}>
								{localLang.moreThePixelMoreTheReach}
							</Typography>
							<div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", width: "100%" }}>
								<Typography
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: "1rem",
										fontSize: "0.75rem",
										fontWeight: 400,
									}}
								>
									<DoneOutlinedIcon sx={{ color: theme.palette.primary.main }} />
									{router.locale === "ar-AE"
										? "قم بتحميل صور واضحة فقط، فهذا سيساعدك على زيادة التحويلات لعقارك."
										: router.locale === "kn-IN"
											? "ದಯವಿಟ್ಟು ಸ್ಪಷ್ಟವಾದ ಚಿತ್ರಗಳನ್ನು ಮಾತ್ರ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ, ಇದರಿಂದ ನಿಮ್ಮ ಆಸ್ತಿಯ ಪರಿವರ್ತನೆಗಳನ್ನು ಹೆಚ್ಚಿಸಲು ಸಹಾಯವಾಗುತ್ತದೆ."
											: "Upload only clear images this will help you increase conversions for your property"}
								</Typography>

								<Typography
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: "1rem",
										fontSize: "0.75rem",
										fontWeight: 400,
									}}
								>
									<DoneOutlinedIcon sx={{ color: theme.palette.primary.main }} />
									{router.locale === "ar-AE"
										? "يجب ألا تحتوي الصور على محتوى ضبابي، لأنه قد يسبب مشكلات للمستخدمين في الفهم."
										: router.locale === "kn-IN"
											? "ಚಿತ್ರಗಳಲ್ಲಿ ಮುಸುಕು ಕಂಡಿರುವ ವಿಷಯಗಳಿರಬಾರದು, ಇದು ಬಳಕೆದಾರರು ಅರ್ಥಮಾಡಿಕೊಳ್ಳಲು ತೊಂದರೆ ಉಂಟುಮಾಡಬಹುದು."
											: "Images should not contain blurry content, as it can create problems for users to understand."}
								</Typography>
								<BeegruButton
									variant="contained"
									sx={{
										width: "100%",
										height: "3rem",
										backgroundColor: theme.palette.primary.main,
										color: theme.palette.common.white,
										"&:hover": { backgroundColor: theme.palette.primary.dark },
									}}
									onClick={() => input.current?.click()}
								>
									{localLang.startUploading}
								</BeegruButton>
							</div>
						</div>
					)} */}

					{donePhotos.length > 1 && (
						<div
							style={{
								position: "absolute",
								bottom: "3.5rem",
								left: "1rem",
								right: "1rem",
								width: "calc(100% - 2rem)",
								display: "flex",
								alignItems: "center",
								gap: "0.5rem",
								padding: "0.5rem 1rem",
								backgroundColor: "#FFC800",
								borderRadius: "8px",
								boxSizing: "border-box",
							}}
						>
							<InfoOutlinedIcon />
							<Typography
								variant="caption"
								sx={{ color: "#666", fontSize: "0.75rem", fontWeight: 400 }}
							>
								{localLang.coverImageText}
							</Typography>
						</div>
					)}
				</CustomDialogContent>
			</CustomDialog>
		</React.Fragment>
	);
};

export default UploadImages;
